import { NgClass } from '@angular/common';
import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { ARTICLE_PAGE_MODEL, ARTICLE_TAG_MODEL } from '@banyan/commons/consts/builder-io-model.const';
import { TagsCategoryDto } from '@banyan/commons/dtos/tags-category.dto';
import { Article } from '@banyan/commons/models/article/article.model';
import { SelectedTags } from '@banyan/commons/models/article/selected-tags.model';
import { TagCategory } from '@banyan/commons/models/article/tag-category.model';
import { BuilderService } from '@banyan/modules/builder-io/services/builder.service';
import { ButtonComponent } from '@banyan/modules/standalone/button/button.component';
import { ChipComponent } from '@banyan/modules/standalone/chip/chip.component';
import { GradientComponent } from '@banyan/modules/standalone/gradient/gradient.component';
import { TrendArticleCardComponent } from '@banyan/modules/standalone/trend-article-card/trend-article-card.component';
import { LangChangeEvent, TranslateModule, TranslateService } from '@ngx-translate/core';
import { DeviceDetectorService } from 'ngx-device-detector';
import { from, Subject, takeUntil } from 'rxjs';

@Component({
  selector: 'app-trends-inspiration',
  standalone: true,
  imports: [
    GradientComponent,
    TranslateModule,
    MatCheckbox,
    ButtonComponent,
    ChipComponent,
    MatIcon,
    NgClass,
    TrendArticleCardComponent,
    MatPaginator,
  ],
  templateUrl: './trends-inspirations.component.html',
  styleUrls: ['./trends-inspirations.component.scss'],
})
export class TrendsInspirationsComponent implements OnInit, OnDestroy {
  translateService = inject(TranslateService);
  deviceService = inject(DeviceDetectorService);
  builderService = inject(BuilderService);
  defaultTagsOptions: any = {
    locale: this.translateService.currentLang || 'en',
    fields: 'id,data.label,data.category.value.id,data.category.value.name',
    limit: 50,
    noTargeting: true,
  };
  destroy = new Subject<void>();
  articles: Article[] = [];
  tagsCategory: TagCategory[] = [];
  selectedTags: SelectedTags[] = [];
  totalArticles: number = 0;
  pageSize: number = 10;
  pageIndex: number = 0;
  globalOptions = {
    locale: this.translateService.currentLang || 'en',
    noTargeting: true,
  };
  isMobileFilterExpanded: boolean = false;

  defaultArticlesOptions: any;
  mainArticleImageWidth = 920;
  articleImageWidth = 450;

  constructor() {}

  ngOnInit(): void {
    this.initComponent();
    if (this.deviceService.isTablet()) {
      this.mainArticleImageWidth = 720;
      this.articleImageWidth = 720;
    }
    if (this.deviceService.isMobile()) {
      this.mainArticleImageWidth = 400;
      this.articleImageWidth = 400;
    }
  }

  initializeOptions(pageIndex: number, pageSize: number): void {
    this.pageIndex = pageIndex;
    this.pageSize = pageSize;
    const query: any = {};
    if (this.selectedTags.length > 0) {
      query['data.tags.tag.id'] = {
        $and: this.selectedTags.map(tag => tag.id),
      };
    }
    this.defaultArticlesOptions = {
      fields:
        'data.publicationDate,data.title,data.abstract,data.image,data.url',
      query: query,
      limit: this.pageSize,
      offset: this.pageIndex * this.pageSize,
      locale: this.translateService.currentLang || 'en',
      noTargeting: true,
    };
  }

  initComponent(): void {
    this.totalArticlesCount(100, 0);
    this.initializeOptions(0, 10);
    this.getTagsCategory();
    this.getFilteredArticles();

    this.translateService.onLangChange
      .pipe(takeUntil(this.destroy))
      .subscribe((event: LangChangeEvent) => {
        this.defaultTagsOptions.locale = event.lang;
        this.defaultArticlesOptions.locale = event.lang;
        this.getTagsCategory();
        this.getFilteredArticles();
      });
  }

  getTagsCategory(): void {
    from(this.builderService.fetchData(ARTICLE_TAG_MODEL, this.defaultTagsOptions))
      .pipe(takeUntil(this.destroy))
      .subscribe((results: any) => {
        this.tagsCategory = TagsCategoryDto(results);
      });
  }

  getFilteredArticles(): void {
    from(this.builderService.fetchData(ARTICLE_PAGE_MODEL, this.defaultArticlesOptions))
      .pipe(takeUntil(this.destroy))
      .subscribe(results => {
        const parsedResults: any[] = [];
        results.forEach(r => {
          parsedResults.push(r.data);
        });
        this.articles = parsedResults;
        this.updateImageParams();
      });
  }

  updateImageParams(): void {
    if (this.articles.length) {
      this.articles.forEach((article, i) => {
        article.image = `${article.image}?width=${i === 0 ? this.mainArticleImageWidth : this.articleImageWidth}&format=webp`;
      });
    }
  }

  totalArticlesCount(limit: number, offset: number): void {
    const query: any = {};
    if (this.selectedTags.length > 0) {
      query['data.tags.tag.id'] = {
        $and: this.selectedTags.map(tag => tag.id),
      };
    }
    const options = {
      fields: 'id',
      query: query,
      limit: limit,
      offset: offset,
      ...this.globalOptions,
    };

    from(this.builderService.fetchData(ARTICLE_PAGE_MODEL, options))
      .pipe(takeUntil(this.destroy))
      .subscribe(response => {
        const results = response;
        this.totalArticles = offset + results.length;
        if (results.length === limit) {
          this.totalArticlesCount(limit, offset + limit);
        }
      });
  }

  onPageChange(event: any) {
    this.initializeOptions(event.pageIndex, event.pageSize);
    this.getFilteredArticles();
  }

  onCheckboxChange(categoryId: string, tagId: string, tagName: string): void {
    const index = this.selectedTags.findIndex(
      tag => tag.categoryId === categoryId && tag.id === tagId
    );
    if (index === -1) {
      this.selectedTags.push({ categoryId, id: tagId, name: tagName });
    } else {
      this.selectedTags.splice(index, 1);
    }
    this.totalArticlesCount(100, 0);
    this.initializeOptions(0, 10);
    this.getFilteredArticles();
  }

  updateSelectedTags(
    categoryId: string,
    tagId: string,
    tagName: string,
    selected: boolean
  ) {
    if (selected) {
      this.selectedTags.push({ categoryId, id: tagId, name: tagName });
    } else {
      this.selectedTags = this.selectedTags.filter(
        tag => !(tag.categoryId === categoryId && tag.id === tagId)
      );
    }
    this.totalArticlesCount(100, 0);
    this.initializeOptions(0, 10);
    this.getFilteredArticles();
  }

  isTagDisabled(categoryId: string, tagId: string): boolean {
    return this.selectedTags.some(
      tag => tag.categoryId === categoryId && tag.id !== tagId
    );
  }

  isChecked(categoryId: string, tagId: string): boolean {
    return this.selectedTags.some(
      tag => tag.categoryId === categoryId && tag.id === tagId
    );
  }

  clearSelectedTags(): void {
    this.selectedTags = [];
    this.totalArticlesCount(100, 0);
    this.initializeOptions(0, 10);
    this.getFilteredArticles();
  }

  toggleMobileFilterBlock(): void {
    this.isMobileFilterExpanded = !this.isMobileFilterExpanded;
  }

  ngOnDestroy(): void {
    this.destroy.complete();
    this.destroy.next();
  }
}
